import { styled } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { IListItem } from "../../interface/list-item";
import { ITreeItem } from "../../interface/tree-item";
import { IWithClassName, IWithThemeProps } from "../../theme/default.theme";
import MultiSelect from "../multi-select/multi-select";
import DrawNode from "./draw-node";

interface IOwnProps {
    selectedItems: Array<IListItem>;
    items: Array<ITreeItem>;
    onChange?: (list: Array<IListItem>) => void;
    placeHolder?: string;
    multiline?: boolean;
    clearAllOption?: boolean;
}

const MultiSelectTree = ({
    selectedItems,
    items: initialItems,
    onChange,
    multiline,
    className,
    clearAllOption,
}: IOwnProps & IWithClassName) => {
    const selectedIds = useMemo(
        () =>
            selectedItems.reduce((map, selected) => {
                map.add(selected.id);
                return map;
            }, new Set<string>()),
        [selectedItems],
    );

    const [items, setItems] = useState(initialItems);
    const [isFiltered, setFiltered] = useState(false);

    useEffect(() => {
        setItems(initialItems);
        setFiltered(false);
    }, [initialItems]);

    const handleSearch = (text: string) => {
        let newItems = initialItems;
        let filtered = false;
        if (text) {
            filtered = true;
            const lowerText = text.toLowerCase();

            const parentSearch = initialItems.filter(
                ({ name }) => name.toLowerCase().indexOf(lowerText) >= 0,
            );

            const childrenSearch = initialItems
                .filter(
                    (parent) =>
                        !parentSearch.find(({ id }) => id === parent.id),
                )
                .map((parent) => ({
                    ...parent,
                    children: parent.children?.filter(
                        (child) =>
                            child.name.toLowerCase().indexOf(lowerText) >= 0,
                    ),
                }))
                .filter((parent) => parent.children?.length);

            newItems = parentSearch
                .concat(childrenSearch)
                .sort((a, b) => a.name.localeCompare(b.name));
        }
        setFiltered(filtered);
        setItems(newItems);
    };

    const handleSingleChange = (item: IListItem, isChecked: boolean) => {
        handleMultipleChange([item], isChecked);
    };

    const handleMultipleChange = (
        items: Array<IListItem>,
        isChecked: boolean,
    ) => {
        if (isChecked) {
            onChange?.([...selectedItems, ...items]);
        } else {
            const deselectIds = items.reduce((map, item) => {
                map.add(item.id);
                return map;
            }, new Set<string>());
            onChange?.(
                selectedItems.filter((item) => !deselectIds.has(item.id)),
            );
        }
    };

    return (
        <>
            <MultiSelect
                selectedValue={selectedItems}
                onChangeHandler={handleSingleChange}
                multiline={multiline}
                onSearchTextChange={handleSearch}
                clearAllOption={clearAllOption}
                onChange={onChange}
            >
                <div className={className}>
                    {items.map((data) => (
                        <DrawNode
                            forceExpand={isFiltered}
                            node={data}
                            selectedIds={selectedIds}
                            onChange={handleMultipleChange}
                            key={data.id}
                        />
                    ))}
                </div>
            </MultiSelect>
        </>
    );
};

export default styled(MultiSelectTree)(({ theme }: IWithThemeProps) => ({
    ".MuiSvgIcon-root": {
        height: theme?.spacing(2),
        cursor: "pointer",
    },
}));
